/**
 * Le pagine che si leggono in ufficio.
 *
 * Chi legge è seduto, ha mouse e tastiera, e spesso sta rispondendo al telefono
 * a qualcuno che è al banco. Per questo l'ufficio vede **anche** le pagine del
 * banco: sono la stessa pagina che ha davanti la persona dall'altra parte.
 *
 * Qui il tono resta lo stesso — corto, pratico — ma il contenuto è quello di
 * chi decide: chi entra, chi vede cosa, dove finiscono i fogli.
 */
export const PAGINE_UFFICIO = [
    {
        k: 'ufficio-riepilogo',
        station: 'ufficio',
        title: 'Riepilogo',
        summary: 'La porta d’ingresso dell’ufficio.',
        roles: ['amministratore'],
        screen: '/',
        sections: [
            {
                title: 'Le schede',
                body: [
                    'C’è una scheda per ogni schermata che il tuo ruolo apre.',
                    'Sotto il nome c’è la parte del lavoro a cui appartiene: Magazzino, Produzione, Amministrazione.',
                    'Premi una scheda per entrare.',
                ],
            },
            {
                title: 'I numeri del turno',
                body: [
                    'Si vedono solo se il tuo ruolo apre la Pianificazione: vengono da lì.',
                    '**Ore impegnate** — le ore del piano contro le ore delle operatrici. In rosso vuol dire che il piano non ci sta.',
                    '**Scarto stime** — quanto il lavoro chiuso è durato più o meno del previsto. «—» finché nessuna lavorazione è chiusa.',
                    '**Cassette in coda** — le cassette che aspettano un reparto.',
                    '**Ordini urgenti** — gli ordini segnati urgenti nel piano.',
                    '**Ordini fermi** — gli ordini a cui manca merce.',
                    'Per capire un numero, apri la Pianificazione.',
                ],
            },
        ],
    },
    {
        k: 'ufficio-reparto-dal-vivo',
        station: 'ufficio',
        title: 'Reparto dal vivo',
        summary: 'Il reparto in 3D: dove sono le cassette adesso, e cosa c’è nei Modula.',
        roles: ['amministratore'],
        screen: '/live-floor',
        sections: [
            {
                title: 'Cosa vedi',
                body: [
                    'Il reparto visto dall’alto, con le sue stazioni: Ricezione, Baia Modula, i tre Modula, Area stampa, Sala cucito, Fine linea.',
                    'Ogni cassetta è un blocchetto nella stazione dove si trova adesso.',
                    'Si aggiorna da solo: non serve ricaricare la pagina.',
                    'In alto a destra **Live** vuol dire collegato. **Riconnessione…** vuol dire che il collegamento è caduto e sta riprovando.',
                ],
            },
            {
                title: 'I quattro contatori',
                body: [
                    '**Spedite oggi** — le cassette uscite oggi dal reparto.',
                    '**In reparto** — le cassette dentro adesso.',
                    '**Ferme** — le cassette rimaste troppo in una stazione.',
                    '**Rovinati** — i capi rovinati in lavorazione.',
                    'Premi un contatore: a destra compare il suo elenco.',
                ],
            },
            {
                title: 'Quando una cassetta è ferma',
                body: [
                    'Diventa rossa quando resta in una stazione più del tempo previsto.',
                    'Ricezione: un’ora. Baia Modula: mezz’ora. Area stampa e Sala cucito: quattro ore. Fine linea: due ore.',
                    'Nei Modula non è mai ferma: lì la merce può restare quanto serve.',
                ],
            },
            {
                title: 'Guardare',
                steps: [
                    {
                        do: 'Premi una cassetta.',
                        then: 'A destra compaiono ordine, cliente, stazione e capi rovinati.',
                    },
                    { do: 'Premi una stazione.', then: 'Vedi quante cassette ha e quante sono ferme.' },
                    { do: 'Usa + e − per avvicinare e allontanare.' },
                    { do: 'Premi la casetta per tornare alla vista iniziale.' },
                ],
            },
            {
                title: 'I cassetti dei Modula',
                body: [
                    'Sopra ogni Modula c’è il numero dei pezzi che contiene, letto dal magazzino.',
                    'Lo vede solo chi può vedere le giacenze.',
                ],
                steps: [
                    {
                        do: 'Premi un Modula.',
                        then: 'A destra compaiono i suoi cassetti, con il disegno degli scomparti e l’ora della lettura.',
                    },
                    { do: 'Per rileggere adesso premi «Aggiorna dal Modula».' },
                ],
            },
            {
                title: 'Se i cassetti non si vedono',
                body: [
                    '«Il Modula non ha risposto» non vuol dire che i cassetti sono vuoti. Riprova fra poco.',
                    '«Il Modula non è collegato» vuol dire che questo sistema non lo legge: chiedi a chi gestisce il server.',
                    'Qui non si sposta niente: la merce nel Modula si muove con le missioni.',
                ],
            },
            {
                title: 'Ultimi movimenti',
                body: [
                    'In basso a sinistra: cosa è successo da quando hai aperto la pagina.',
                    'Parte vuoto ogni volta. Non è uno storico.',
                ],
            },
        ],
    },
    {
        k: 'ufficio-stato-reparto',
        station: 'ufficio',
        title: 'Stato del reparto',
        summary: 'Un semaforo per ogni pezzo: il reparto funziona, e se no, cosa si è fermato.',
        roles: ['amministratore'],
        screen: '/shop-floor-status',
        sections: [
            {
                title: 'Cosa vedi',
                body: [
                    'Sette righe, una per pezzo: spool di reparto, rete verso il cloud, Modula, cartella etichette, bilancia, database, DecoNetwork.',
                    '**Verde** — va. **Giallo** — funziona ma c’è qualcosa da guardare. **Rosso** — fermo. **Grigio** — non lo so dire.',
                    'Accanto al colore c’è il motivo e, dove si sa, l’ultima volta che ha funzionato.',
                    'La rete diventa gialla anche per una sola risposta del Modula persa verso il cloud: è un comando che qualcuno ha visto fallire, anche se il Modula aveva risposto.',
                    'Sotto i semafori, «Quello che vede il cloud» elenca versione, database, catalogo, magazzino, spool e la versione che lo spool dichiara.',
                ],
            },
            {
                title: 'Il bollettino dello spool',
                body: [
                    'Lo spool manda ogni minuto un bollettino. Il blocco «Quello che dice lo spool» mostra, per ogni pezzo, i numeri: versione e da quanto è acceso lo spool, chiamate al cloud e quante sono fallite, le risposte del Modula che non sono arrivate al cloud, comandi al Modula con i tempi, spazio libero nelle cartelle, coda della bilancia. Sotto trovi i tempi del Modula per ogni comando e gli ultimi cinque errori.',
                    'Il Modula diventa rosso solo se non si riesce a raggiungerlo; se risponde ma rifiuta dei comandi (per esempio una password sbagliata) diventa giallo e la pagina ne dice il numero.',
                    'Se durante il collaudo del Modula lo spool è in pausa, la pagina scrive «In pausa per il collaudo» e lo spool è giallo, non rosso: è voluto.',
                    'Se non è arrivato nessun bollettino, la pagina lo scrive: vuol dire che lo spool non parla, non che va tutto bene.',
                    'Se l’ultimo bollettino è vecchio, la pagina lo scrive e sbiadisce i dati: non fidarti. Se invece solo l’orologio dello spool è sfasato, la pagina lo avvisa e lo spool diventa giallo, ma i dati restano leggibili.',
                    'La pagina si aggiorna da sola ogni 15 secondi; «Aggiorna» lo fa subito.',
                ],
            },
        ],
    },
    {
        k: 'ufficio-utenti',
        station: 'ufficio',
        title: 'Utenti e ruoli',
        summary: 'Chi entra, e cosa vede una volta entrato.',
        roles: ['amministratore'],
        screen: '/users',
        sections: [
            {
                title: 'I cinque ruoli',
                body: [
                    '**Ricezione** — il banco: riceve la merce, conta, etichetta, conferma le cassette.',
                    '**Magazzino** — carico in baia, scarico, prelievi.',
                    '**Produzione** — stampa e ricamo. In ufficio apre la pianificazione, l’archivio esecutivi e i capi rovinati in lavorazione.',
                    '**Fine linea** — imballaggio e spedizione.',
                    '**Amministratore** — vede tutto, e in più gestisce utenti, operatrici e deroghe.',
                    'Il ruolo decide cosa si vede: chi non ce l’ha non apre la schermata nemmeno scrivendone l’indirizzo.',
                ],
            },
            {
                title: 'Creare e togliere',
                figure: {
                    file: 'ufficio-utenti.png',
                    alt: 'L’elenco degli utenti, con ruoli e stato.',
                    width: 1440,
                    height: 900,
                },
                body: [
                    'Un utente ha un login, una password e uno o più ruoli.',
                    'Le password sono personali e valgono come firma: non si condividono fra postazioni.',
                    'Un utente che se ne va si **disattiva**, non si cancella: quello che ha fatto resta attribuito a lui.',
                    'Si **cancella** solo chi non è mai entrato, per esempio un utente creato per sbaglio: non ha fatto niente che debba restargli attribuito.',
                    'Il sistema non ti lascia togliere l’ultimo amministratore, né disattivare te stesso: sarebbe chiudersi fuori.',
                ],
            },
            {
                title: 'Perché a volte richiede la password',
                body: [
                    'Alcuni gesti scrivono un nome accanto a un fatto: chi ha creato un utente, chi ha forzato un parziale, chi ha chiuso una lavorazione.',
                    'Prima di quei gesti il sistema richiede la password. Non è un nuovo accesso: è la firma.',
                    'Vale cinque minuti, poi la richiede di nuovo. È breve apposta: una postazione lasciata incustodita parla a nome di chi l’ha lasciata.',
                ],
            },
            {
                title: 'Il primo amministratore',
                body: [
                    'Nasce dalla variabile `AUTH_ADMIN_INIZIALE`, nella forma `login:password`, e solo quando non c’è ancora nessun utente.',
                    'Al primo accesso cambia la password e togli quella variabile dall’ambiente.',
                ],
            },
        ],
    },
    {
        k: 'ufficio-persone',
        station: 'ufficio',
        title: 'Pianificazione',
        summary: 'Chi lavora su cosa, ora per ora.',
        roles: ['amministratore', 'produzione'],
        screen: '/planning',
        sections: [
            {
                title: 'Il calendario',
                body: [
                    'È il calendario a decidere il turno, non un interruttore.',
                    'Fuori orario e in pausa i timer di produzione non corrono.',
                    'La regia può forzare il turno, e quando lo fa il sistema lo dichiara.',
                ],
            },
            {
                title: 'Il piano del giorno',
                figure: {
                    file: 'ufficio-pianificazione.png',
                    alt: 'La plancia del giorno: la striscia delle ore in alto, le operatrici in colonna.',
                    width: 1440,
                    height: 900,
                },
                body: [
                    'La striscia in alto sono le ore del turno, col segno su quella corrente: è la scala sotto cui si legge ogni numero della pagina.',
                    'Le colonne sono le operatrici, le schede sono gli ordini. Si trascina.',
                    'La pre-assegnazione è un suggerimento: guida, non impedisce.',
                    'Un giorno passato si legge e non si cambia.',
                    '**Il piano si compone da solo alle 16:00**, e prende gli ordini di oggi e di domani da DecoNetwork. Finché un ordine non è nel piano, il magazzino non chiama le sue cassette in baia.',
                    '«Componi il piano» lo rifà adesso. Serve quando il giro delle quattro non è andato — DecoNetwork giù — o quando è entrato un ordine dopo. Rifarlo non disfa niente: le deroghe, le assegnazioni e gli ordini già partiti in produzione restano dove sono.',
                    'Un ordine esce dal piano solo quando la sua spedizione è chiusa. Un ordine in ritardo resta, ed è voluto: si continua a lavorarlo.',
                ],
            },
        ],
    },
    {
        k: 'ufficio-operatrici',
        station: 'ufficio',
        title: 'Operatrici',
        summary: 'Chi lavora in produzione, in che reparto e con quante ore.',
        roles: ['amministratore'],
        screen: '/operators',
        sections: [
            {
                title: 'Operatrici',
                figure: {
                    file: 'ufficio-operatrici.png',
                    alt: 'L’organico della produzione: reparto, ore, legame con l’accesso.',
                    width: 1440,
                    height: 900,
                },
                body: [
                    'Qui si dice chi lavora in produzione, in quale reparto e per quante ore.',
                    'Non è la stessa cosa degli utenti: un’operatrice è organico, un utente è un accesso. Si possono legare, ma restano due elenchi.',
                    'Le ore di un’operatrice diventano le colonne della pianificazione: si cambiano qui, non lì.',
                ],
            },
            {
                title: 'Aggiungere e cambiare',
                steps: [
                    { do: 'Premi «Nuova operatrice».' },
                    { do: 'Scrivi nome, reparto e ore di turno.' },
                    {
                        do: 'Scegli l’accesso collegato, se entra col suo login.',
                        then: 'Senza, le ore del suo timer finiscono fra le «ore non attribuite» della pianificazione.',
                    },
                    { do: 'Premi «Salva».' },
                ],
            },
            {
                title: 'Cambiare e togliere',
                body: [
                    'Per cambiarla, apri i gesti a fine riga e scegli «Modifica».',
                    '«Manda fuori forza» la toglie dalla plancia. «Rimetti in forza» la riporta: niente si perde.',
                ],
            },
        ],
    },
    {
        k: 'ufficio-giacenze',
        station: 'ufficio',
        title: 'Giacenze',
        summary: 'Quanti ne abbiamo, secondo DecoNetwork e secondo il magazzino.',
        roles: ['magazzino'],
        screen: '/stock',
        sections: [
            {
                title: 'Perché ci sono due colonne',
                body: [
                    'Le fonti sono due e non si parlano.',
                    '**DecoNetwork** tiene la giacenza commerciale: quella che decide se un ordine si può accettare.',
                    '**Il magazzino** tiene quella fisica: quella che dice se il capo c’è davvero dentro una macchina.',
                    'Questa schermata non sceglie chi ha ragione. Le mette in fila e mostra dove litigano — ed è il litigio il dato utile.',
                ],
            },
            {
                title: '«Mai letto» non vuol dire zero',
                body: [
                    'È la cosa più importante di questa pagina.',
                    'Una colonna che dice «mai letto» vuol dire che quella fonte non ha ancora parlato. Non vuol dire che non c’è niente.',
                    'Finché i magazzini automatici non sono collegati, la colonna «Magazzino» dice «mai letto» su tutto. È giusto così: inventare uno zero farebbe suonare un allarme su ogni riga del catalogo.',
                    'In cima alla schermata ci sono due date, una per fonte: dicono quando ciascuna ha parlato l’ultima volta.',
                ],
            },
            {
                title: 'Guardare',
                steps: [
                    { do: 'Cerca l’articolo, il codice, lo sku, la taglia o il colore.' },
                    {
                        do: 'Scegli «Solo scostamenti» per vedere dove le due fonti non vanno d’accordo.',
                        then: 'Ci finiscono anche le righe che una delle due non conosce affatto.',
                    },
                    { do: 'Scegli «Solo sotto scorta» per vedere cosa sta per finire.' },
                ],
            },
            {
                title: 'I cinque stati',
                body: [
                    '**Non torna** — i due numeri ci sono e sono diversi. È il primo da guardare.',
                    '**Sotto scorta** — DecoNetwork è arrivato alla soglia di riordino.',
                    '**Solo magazzino** — il magazzino ha qualcosa che DecoNetwork non sa di avere.',
                    '**Solo DecoNetwork** — il magazzino non ne ha mai parlato. Prima di settembre è lo stato normale di tutto.',
                    '**Allineata** — i due numeri coincidono.',
                    'La colonna «Differenza» porta il segno: **+3** vuol dire che DecoNetwork ne conta tre più del magazzino.',
                ],
            },
            {
                title: 'Le cassette nelle macchine',
                body: [
                    'In fondo, quando ce ne sono, si vedono le cassette che il magazzino tiene come se fossero articoli.',
                    'Il magazzino non ne conosce il contenuto: sa quante ne ha, non cosa c’è dentro. Per questo stanno a parte e non fanno una riga di giacenza.',
                ],
            },
            {
                title: 'Qui non si corregge niente',
                body: [
                    'Non c’è nessun pulsante che cambi una giacenza, ed è voluto.',
                    'Nessuno dei due numeri è nostro: uno è di DecoNetwork, l’altro del magazzino. Correggerli da qui creerebbe una terza verità che nessuno rimette a posto.',
                    'Quando i due numeri litigano, si sistema alla fonte — con una missione al magazzino o una rettifica su DecoNetwork.',
                ],
            },
        ],
    },
    {
        k: 'ufficio-cassetti',
        station: 'ufficio',
        title: 'Cassetti',
        summary: 'Cosa c’è in ogni cassetto del Modula, letto adesso dal magazzino.',
        roles: ['magazzino'],
        screen: '/drawers',
        sections: [
            {
                title: 'Cosa si vede',
                body: [
                    'Una scheda per cassetto, raggruppate per macchina. In cima alla scheda: pezzi, scomparti e scomparti vuoti.',
                    'Il disegno è in scala: il cassetto è visto dall’alto, con il fondo in alto e la **baia in basso** (lo dice «▼ baia» sotto ogni cassetto).',
                    'In ogni scomparto: il codice dell’articolo, la descrizione e **giacenza / capienza**.',
                    'Una scatola con più colori mostra dentro di sé l’elenco dei suoi articoli. Uno scomparto tratteggiato è vuoto.',
                ],
            },
            {
                title: 'Letto adesso, non conservato',
                body: [
                    'Ogni volta che apri la schermata, o premi «Aggiorna dal Modula», i cassetti si leggono dal magazzino in quel momento. Sotto il titolo c’è l’ora della lettura.',
                    'Un cassetto senza scomparti configurati non compare: se manca un numero, quel cassetto non ha scomparti.',
                ],
            },
            {
                title: '«Non lo so» non vuol dire vuoto',
                body: [
                    'Se il Modula non risponde la schermata lo dice, e non mostra cassetti vuoti. Riprova fra poco.',
                    'Se il Modula non è collegato, la schermata dice che i cassetti non sono mai stati letti.',
                ],
            },
            {
                title: 'Qui non si sposta niente',
                body: ['La merce nel Modula si muove con le missioni, non da questa schermata.'],
            },
        ],
    },
    {
        k: 'ufficio-missioni-carico',
        station: 'ufficio',
        title: 'Missioni di caricamento',
        summary: 'Cosa mettere nel Modula: tu scegli articoli e pezzi, la cella la sceglie il Modula.',
        roles: ['magazzino'],
        screen: '/loading-missions',
        // The rules told here are those of `pezziDaCaricare`, `aggiungiTutti` and
        // `parseCodeFilter`: change one, change this text.
        sections: [
            {
                title: 'I passi',
                steps: [
                    {
                        do: 'Premi «Nuova missione».',
                        then: 'Nasce una bozza col suo codice per il Modula.',
                    },
                    { do: 'Scrivi una descrizione e scegli il magazzino.' },
                    { do: 'Cerca un articolo, controlla i pezzi e premi «Aggiungi».' },
                    { do: 'Premi «Salva bozza» per continuare dopo, oppure «Invia al Modula».' },
                    {
                        do: 'Dopo l’invio premi «Aggiorna dal Modula».',
                        then: 'Per ogni riga vedi quanti pezzi sono entrati e in quale cassetto.',
                    },
                ],
            },
            {
                title: 'Inviata non vuol dire caricata',
                body: [
                    '**Inviata** vuol dire che il Modula ha ricevuto l’ordine, non che l’ha caricato.',
                    'Una missione è fatta solo quando il Modula lo dice.',
                ],
            },
            {
                title: 'I pezzi proposti',
                body: [
                    'Quando scegli un articolo, i pezzi partono da **DecoNetwork meno Modula**: quello che manca nel Modula.',
                    'Se il Modula ne ha già tanti o di più: 0.',
                    'Se DecoNetwork non conta l’articolo: 1.',
                    'Puoi sempre cambiarli.',
                ],
            },
            {
                title: 'La ricerca',
                body: [
                    'Una parola cerca nel codice, nel nome, nel colore e nella taglia. Per esempio **navy**.',
                    'Per prendere più codici insieme scrivi una condizione:',
                    '**91110%** — i codici che iniziano con 91110. Il % vale «qualsiasi cosa».',
                    '**>9111010** — i codici dopo 9111010. Con >= anche 9111010.',
                    '**<=9111050** — i codici fino a 9111050 compreso. Con < senza.',
                    '**9111010..9111050** — i codici fra i due, estremi compresi.',
                    'Una condizione guarda solo il codice: lo SKU di catalogo, o il codice prodotto se lo SKU manca.',
                    'I codici fatti solo di cifre si confrontano come numeri: dopo 99 viene 100.',
                ],
            },
            {
                title: 'Aggiungi tutti',
                body: [
                    'Con una condizione compare **Aggiungi tutti**: mette nella bozza tutti gli articoli trovati, anche quelli che la lista non mostra.',
                    'Salta quelli con 0 pezzi, quelli che DecoNetwork non conta e quelli già nella bozza: premerlo due volte non raddoppia niente.',
                    'Una missione tiene al massimo 1000 righe.',
                    'Le righe vanno sempre in ordine di codice crescente, ed è l’ordine in cui le riceve il Modula.',
                ],
            },
            {
                title: 'Eliminare o annullare',
                body: [
                    'Una bozza mai inviata si toglie con **Elimina bozza**: sparisce.',
                    'Una missione che il Modula potrebbe avere — inviata, o tentata e andata in errore — non si elimina: si **annulla**.',
                    'Annullata resta nell’elenco sotto **Tutte**, con il suo codice, e i suoi esiti non contano più.',
                    'Sul WMS l’ordine resta: se c’è, toglilo dal WMS.',
                    'Una missione già fatta non si annulla: la merce è nei cassetti.',
                ],
            },
        ],
    },
    {
        k: 'ufficio-scaffale-negozio',
        station: 'ufficio',
        title: 'Scaffale negozio',
        summary: 'Quanti pezzi ci sono sugli scaffali del negozio, e perché.',
        roles: ['magazzino'],
        screen: '/store-shelf',
        sections: [
            {
                title: 'Da dove viene il numero',
                body: [
                    'Lo scaffale del negozio lo teniamo solo noi: DecoNetwork e il Modula non sanno che esiste.',
                    'Il numero è la somma dei movimenti: **Dal Modula**, **Dal banco**, **Venduto in cassa**, **Reso in cassa**, **Rettifica inventario**.',
                    'In fondo alla pagina, gli ultimi movimenti dicono da dove viene ogni differenza.',
                    'Un numero rosso sotto zero vuol dire che una vendita è arrivata prima del carico. Si sistema con una rettifica.',
                    'Ogni riga mostra lo SKU dell’articolo. **senza SKU** vuol dire che la variante a catalogo non ce l’ha: le vendite in cassa non la trovano.',
                ],
            },
            {
                title: 'Rettificare dopo una conta',
                steps: [
                    { do: 'Conta i pezzi sullo scaffale.' },
                    { do: 'Premi «Rettifica» sulla riga dell’articolo.' },
                    { do: 'Scrivi quanti pezzi hai contato.' },
                    {
                        do: 'Premi «Registra la rettifica».',
                        then: 'Si registra la differenza come movimento: il numero vecchio resta nella storia. Se il conto era già giusto, non si registra niente.',
                    },
                ],
                refusals: [
                    {
                        rule: '45',
                        says: 'Conta non valida',
                        why: 'Hai scritto un numero negativo o con la virgola.',
                        fix: 'Scrivi i pezzi interi, da zero in su.',
                    },
                ],
            },
            {
                title: 'Vendite Shopify da abbinare',
                body: [
                    'Compaiono quando una vendita in cassa non si lega a una variante del catalogo.',
                    'Ogni riga dice quando e in quale ordine è stata venduta, il prodotto e la variante come li chiama Shopify, e il fornitore.',
                    'Il motivo è scritto sulla riga: la variante su Shopify non ha SKU, nessuna variante ha quello SKU, o ce l’hanno in più di una.',
                    'Non sono perse: aspettano.',
                ],
                steps: [
                    { do: 'Apri l’ordine su Shopify dal suo numero, se serve vedere di più.' },
                    { do: 'Sistema lo SKU della variante a catalogo.' },
                    { do: 'Premi «Riprova ad abbinare», o aspetta il prossimo giro.' },
                ],
            },
        ],
    },
    {
        k: 'ufficio-cassette-carrelli',
        station: 'ufficio',
        title: 'Cassette e carrelli',
        summary: 'Quali cassette e carrelli esistono, in che stato sono e quanto tengono.',
        roles: ['amministratore'],
        screen: '/equipment-registry',
        sections: [
            {
                title: 'L’elenco',
                body: [
                    'Il banco riempie solo le cassette di questo elenco.',
                    'Il codice è una lettera e un numero senza zeri: si scrive C21, non C021.',
                    'Le etichette da attaccare si stampano da Magazzino › Etichette attrezzatura.',
                ],
            },
            {
                title: 'Aggiungere',
                steps: [
                    {
                        do: 'Per le cassette scrivi la lettera, il primo e l’ultimo numero.',
                        then: 'Premi «Aggiungi intervallo».',
                    },
                    { do: 'Per un carrello scrivi il codice e premi «Aggiungi carrello».' },
                ],
            },
            {
                title: 'In uso, fuori servizio',
                body: [
                    'Una cassetta con merce dentro non si rinomina e non si cancella: i tasti sono spenti.',
                    'Lo stesso per un carrello che porta cassette.',
                    'Si può sempre mettere **fuori servizio**, anche piena. Scrivi nella nota cosa ha.',
                    'Quando è riparata, premi «Rimetti in servizio».',
                ],
            },
            {
                title: 'Capienza',
                body: [
                    'Quanti capi entrano in una cassetta e quante cassette stanno su un carrello.',
                    'Cambia i numeri e premi «Salva».',
                ],
            },
        ],
    },
    {
        k: 'ufficio-etichette-attrezzatura',
        station: 'ufficio',
        title: 'Etichette attrezzatura',
        summary: 'Il codice da attaccare ai carrelli e alle cassette.',
        roles: ['magazzino'],
        screen: '/equipment-labels',
        sections: [
            {
                title: 'È la terza etichetta, e non somiglia alle altre due',
                body: [
                    'Le etichette del banco parlano della **merce**: un gruppo di capi, o cosa c’è dentro una cassetta adesso. Vivono quanto la merce.',
                    'Questa parla del **contenitore**. Si stampa una volta, si attacca, e resta lì finché il carrello o la cassetta esistono.',
                    'Per questo sopra c’è il codice e nient’altro. Un ordine, un cliente o una data stampati qui sarebbero veri il giorno della stampa e falsi tutti gli altri — e nessuno stacca un’etichetta perché è invecchiata: la legge e ci crede.',
                    'Il barcode contiene il codice nudo, lo stesso che si bippa in baia. È quello che serve quando quello serigrafato sulla cassetta è coperto da un capo o graffiato.',
                ],
            },
            {
                title: 'I carrelli si spuntano, le cassette si scrivono',
                body: [
                    'Non è una differenza di comodità: è che le due cose non si comprano allo stesso modo.',
                    'I **carrelli** sono pochi e si scelgono per nome, fra quelli dell’elenco.',
                    'Le **cassette** arrivano a scatoloni, quindi si chiedono a intervallo: una lettera, e da quale numero a quale.',
                    'Si stampa solo per codici **già nell’elenco**. Cassette e carrelli nuovi si aggiungono prima, da Amministrazione › Cassette e carrelli: un’etichetta per un codice che non c’è sarebbe un barcode che il reparto rifiuta al primo bip.',
                    'Il codice è una lettera maiuscola e un numero **senza zeri**: si scrive `C21`, non `C021`.',
                ],
            },
            {
                title: 'Stampare',
                steps: [
                    { do: 'Spunta i carrelli da etichettare, oppure nessuno.' },
                    {
                        do: 'Scrivi da quale cassetta a quale, estremi compresi.',
                        then: 'Con un estremo solo scritto le cassette restano «nessuna»: mezzo intervallo non è un intervallo.',
                    },
                    {
                        do: 'Guarda quante etichette usciranno — è scritto accanto al tasto.',
                        then: 'È l’ultimo momento in cui ci si accorge di uno zero di troppo: dopo, la termica non si ferma a metà.',
                    },
                    {
                        do: 'Premi «Stampa».',
                        then: 'I fogli escono dalla stessa termica delle etichette del banco.',
                    },
                ],
            },
            {
                title: 'Quando non esce niente',
                body: [
                    '**Più di duecento in un colpo** non si stampano, e il tasto si spegne prima. Non è un limite tecnico: è il freno contro «da 1 a 10000», che sarebbe un rotolo intero buttato prima che qualcuno arrivi a spegnere la stampante. Falle in più volte.',
                    '**Un intervallo all’indietro** — dalla 30 alla 10 — viene rifiutato invece di stampare zero etichette in silenzio.',
                    '**Se la cartella di stampa non risponde**, la schermata lo dice e non è uscito niente. Non andare ad aspettare alla termica: riprova, e se insiste è la cartella di rete.',
                ],
            },
        ],
    },
    {
        k: 'ufficio-qualita',
        station: 'ufficio',
        title: 'Qualità: i capi che non ci sono più',
        summary: 'Merce arrivata rotta e capi rovinati da noi. Due cose diverse.',
        roles: ['amministratore', 'produzione'],
        screen: '/quality',
        sections: [
            {
                title: 'Due schede, due colpe',
                body: [
                    '**Dal fornitore** — merce arrivata difettosa. La segnala il banco col tasto «✗ fallato».',
                    '**In lavorazione** — capi rovinati da noi. Li dichiara il reparto durante la stampa o il ricamo.',
                    'Vedi solo la scheda del tuo ruolo. L’amministratore le vede tutte e due.',
                    'La differenza non è formale: il fallato si reclama al fornitore, il capo rovinato si rifà.',
                ],
                figure: {
                    file: 'ufficio-qualita.png',
                    alt: 'La pagina Qualità, scheda «Dal fornitore»',
                    width: 1440,
                    height: 900,
                },
            },
            {
                title: 'Dal fornitore',
                body: [
                    'Ogni riga dice quando, quale acquisto e riga, l’articolo, la taglia, quanti capi e chi l’ha segnalato.',
                    'Se al banco è stata scattata una foto, la miniatura la apre: è la prova da mandare al fornitore.',
                    'La merce resta **non ricevuta**: l’ordine d’acquisto resta aperto per quella quantità.',
                ],
                steps: [
                    { do: 'Guarda l’elenco, che si apre su «Da prendere in carico».' },
                    { do: 'Parla col fornitore.' },
                    {
                        do: 'Premi «Presa in carico».',
                        then: 'La riga esce dai da fare e l’avviso nella campanella si spegne.',
                    },
                ],
                refusals: [
                    {
                        rule: '8-bis',
                        says: 'Già presa in carico',
                        why: 'Qualcun altro se n’è già occupato, e il suo nome è sulla riga.',
                        fix: 'Non rifarlo. Se serve parlarne ancora, parlane con chi l’ha presa.',
                    },
                ],
            },
            {
                title: 'In lavorazione',
                body: [
                    'Ogni riga dice l’ordine, il cliente, l’articolo, la **taglia** da rifare, il reparto e la causale.',
                    'La causale è obbligatoria al banco: macchina, filato, impostazione, difetto del capo, altro. Con «altro» chi dichiara scrive cos’è successo, e qui si legge.',
                    'Questi capi mancano all’ordine del cliente. Se non li rifai, a fine linea la spedizione sarà incompleta.',
                ],
                steps: [
                    { do: 'Guarda l’elenco, che si apre su «Da reintegrare».' },
                    { do: 'Rifai i capi: la taglia è scritta sulla riga.' },
                    {
                        do: 'Premi «Reintegrato».',
                        then: 'La riga si chiude col tuo nome e l’avviso si spegne.',
                    },
                ],
                refusals: [
                    {
                        rule: '20-quater',
                        says: 'Già reintegrato',
                        why: 'Qualcun altro ha già rifatto quei capi.',
                        fix: 'Non rifarli. Il nome di chi l’ha fatto è sulla riga.',
                    },
                ],
            },
            {
                title: 'Le causali si rileggono',
                body: [
                    'Una causale che torna spesso è un problema da risolvere, non da contare.',
                    'Il conto per causale sta in Pianificazione, scheda «Adesso».',
                    'Se «altro» diventa la voce più usata, vuol dire che manca una voce: dillo.',
                ],
            },
        ],
    },
    {
        k: 'ufficio-esecutivi',
        station: 'ufficio',
        title: 'Archivio esecutivi',
        summary: 'I file che le macchine leggono, tenuti da noi.',
        roles: ['produzione'],
        screen: '/print-files',
        sections: [
            {
                title: 'Perché esiste',
                body: [
                    'DecoNetwork **cancella** gli esecutivi circa due settimane dopo la spedizione.',
                    'Noi li scarichiamo mentre ci sono e li teniamo. Questo è il posto dove si va quando lì non ci sono più.',
                    'Lo scarico gira da solo ogni cinque minuti.',
                ],
                figure: {
                    file: 'ufficio-esecutivi.png',
                    alt: 'L’archivio degli esecutivi, con gli ordini e i loro file',
                    width: 1440,
                    height: 900,
                },
            },
            {
                title: 'Trovare e scaricare',
                steps: [
                    { do: 'Cerca il numero dell’ordine, o il nome del file.' },
                    { do: 'Filtra per lavorazione se serve: DTG, DTF, WEMB, SCR.' },
                    { do: 'Apri la riga dell’ordine.', then: 'Sotto compaiono i suoi file.' },
                    { do: 'Premi «Scarica».' },
                ],
            },
            {
                title: 'I tre stati, e cosa vogliono dire',
                body: [
                    '**In archivio** — c’è tutto, non chiede niente a nessuno.',
                    '**Incompleto** — qualcosa si è perso, ma il resto c’è.',
                    '**Perduto** — quell’ordine non ha più niente da nessuna parte. Si può solo rigenerare a mano, finché l’ordine esiste.',
                    'I file barrati non li abbiamo mai presi e DecoNetwork li ha cancellati. Restano in elenco apposta: dire «non c’è» è un’informazione, nasconderlo è una bugia.',
                ],
            },
            {
                title: 'L’orologio accanto alla riga',
                body: [
                    'Vuol dire che DecoNetwork non ha più quei file, e noi sì.',
                    'È la ragione per cui questa schermata esiste.',
                ],
            },
            {
                title: 'Le versioni',
                body: [
                    'Un esecutivo rigenerato non sovrascrive il vecchio: è una versione nuova.',
                    'Quella vecchia poteva essere già andata in macchina, e serve poterla ritrovare.',
                    'La corrente è segnata. Le altre si aprono premendo «N versioni».',
                ],
            },
        ],
    },
    {
        k: 'ufficio-pesate',
        station: 'ufficio',
        title: 'Pesate',
        summary: 'Le pesate della stazione PS130 arrivate nel cloud.',
        roles: ['amministratore'],
        screen: '/weighings',
        sections: [
            {
                title: 'A cosa serve',
                body: [
                    'Dice se le pesate della stazione arrivano davvero.',
                    'In cima c’è quanto tempo fa è arrivata l’ultima.',
                    'Le colonne: quando, barcode del collo, peso, misure. «non misurato» vuol dire che la stazione non ha mandato quel dato.',
                ],
            },
            {
                title: 'Gli avvisi',
                body: [
                    '**Non legale** — la stazione dice che quel peso non vale come pesata legale.',
                    '**Trama sospetta** — il messaggio della bilancia è arrivato con un controllo che non torna. Rifai la pesata.',
                ],
            },
            {
                title: 'Quando non arriva niente',
                steps: [
                    { do: 'Pesa un collo sul PS130.' },
                    { do: 'Premi «Aggiorna».' },
                    {
                        do: 'Se non compare, controlla il mini-PC di reparto.',
                        then: 'Le pesate che il cloud rifiuta restano lì.',
                    },
                ],
            },
        ],
    },
    {
        k: 'ufficio-registro-deconetwork',
        station: 'ufficio',
        title: 'Registro DecoNetwork',
        summary: 'Tutto quello che abbiamo scritto su DecoNetwork: cosa c’era prima, cosa è partito, cosa è tornato.',
        roles: ['amministratore'],
        screen: '/deconetwork-writes',
        sections: [
            {
                title: 'A cosa serve',
                body: [
                    'Ogni scrittura su DecoNetwork lascia una riga: quando, che comando, su quale PO o ordine, chi l’ha fatta.',
                    'Si filtra per PO o ordine, per esito e per periodo (predefinito: ultimi 30 giorni).',
                ],
            },
            {
                title: 'Gli esiti',
                body: [
                    '**Riuscita** — DecoNetwork ha risposto bene.',
                    '**Fallita** — DecoNetwork ha rifiutato la scrittura.',
                    '**Incerta** — non sappiamo se è passata; accanto si legge se la verifica l’ha trovata scritta o no.',
                    '**Non partita** — la chiamata non è uscita da qui.',
                ],
            },
            {
                title: 'Vedere i dettagli',
                steps: [
                    { do: 'Clicca la riga.', then: 'Si aprono Prima, Mandato e Risposta.' },
                    { do: 'Per i dati grezzi apri «mostra la chiamata grezza».' },
                ],
            },
        ],
    },
    {
        k: 'ufficio-impostazioni',
        station: 'ufficio',
        title: 'Impostazioni',
        summary: 'Le preferenze dell’ufficio. Valgono per tutti.',
        roles: ['amministratore'],
        screen: '/settings',
        sections: [
            {
                title: 'Non sono personali',
                body: [
                    'Quello che si cambia qui vale per tutti quelli che entrano, non solo per te.',
                    'Gli orari di lavoro non stanno qui: stanno in Pianificazione, scheda «Impostazioni».',
                ],
                figure: {
                    file: 'ufficio-impostazioni.png',
                    alt: 'La pagina Impostazioni, con l’interruttore della mail sui fallati',
                    width: 1440,
                    height: 900,
                },
            },
            {
                title: 'La mail sui fallati',
                body: [
                    'Quando il banco segnala un capo fallato, l’avviso nella campanella arriva **sempre**. Da qui non si spegne.',
                    'L’interruttore decide se deve partire **anche** una mail.',
                    'Una mail per segnalazione, non un riepilogo di fine giornata: un fallato di stamattina scoperto stasera è merce già contata.',
                ],
                steps: [
                    {
                        do: 'Scrivi un indirizzo e premi Invio.',
                        then: 'Compare come gettone. La × lo toglie.',
                    },
                    { do: 'Accendi l’interruttore.' },
                    { do: 'Premi «Salva».' },
                ],
            },
            {
                title: 'Due cose da sapere',
                body: [
                    'Senza destinatari l’interruttore **non si accende**: una mail senza indirizzo non è un avviso.',
                    'Togliendo l’ultimo indirizzo si spegne da solo.',
                    '**Oggi la mail finisce nel registro del server, non in una casella.** L’invio vero si accende quando ci saranno le credenziali del server di posta.',
                ],
            },
        ],
    },
    {
        k: 'ufficio-impianto',
        station: 'ufficio',
        title: 'Stampanti, cartelle, collegamenti',
        summary: 'Dove finiscono i fogli, e cosa guardare quando non arrivano.',
        roles: ['amministratore'],
        sections: [
            {
                title: 'Le etichette',
                body: [
                    'Il sistema non parla con la stampante: **deposita PDF in una cartella**, e il programma di stampa se li porta via. È l’impianto che c’è già.',
                    'Il sistema gira in cloud e la cartella sta in reparto: i PDF li porta lo **spool di reparto**, che li scrive nella cartella indicata da `ETICHETTE_CARTELLA_HOST` sul server di reparto. Se lì non è impostata, chi preme *Stampa* legge «La stampa non è partita».',
                    'Se il sistema gira dove vede la cartella, la si indica con `ETICHETTE_CARTELLA` e lo spool non serve. Senza nessuna delle due, le etichette finiscono nel registro invece che su carta — utile per provare, sbagliato in reparto.',
                    'Se le etichette non arrivano, il problema è quasi sempre lì: cartella irraggiungibile, o piena di fogli che nessuno ritira.',
                    'I file non si sovrascrivono: se `cassetta-C12.pdf` c’è ancora, il successivo diventa `cassetta-C12-2.pdf`. Una cartella che non si svuota è un raccoglitore fermo.',
                ],
            },
            {
                title: 'Il QR sulle etichette',
                body: [
                    '`ETICHETTE_URL_BASE` è l’indirizzo con cui il banco si raggiunge dai telefoni.',
                    'Se è vuota le etichette escono **senza QR**, ed è voluto: un quadrato che porta a `localhost`, stampato su cento cassette, è un errore che si scopre in reparto col telefono in mano.',
                    'Il telefono deve stare sulla stessa rete.',
                ],
            },
            {
                title: 'I tre collegamenti',
                body: [
                    'Tre variabili dicono se il sistema parla col mondo vero o con un doppio finto.',
                    '`DECONETWORK_BASE_URL` vuota → catalogo di prova, righe inventate.',
                    '`MODULA_BASE_URL` vuota → magazzino finto, nessuna missione parte.',
                    '`DATABASE_URL` vuota → tutto in memoria, si perde al riavvio.',
                    'Servono per fare una dimostrazione. In produzione il sistema si rifiuta di partire così.',
                    'Quando qualcosa non torna, la prima domanda è quale delle tre è vuota.',
                ],
            },
            {
                title: 'Gli esecutivi di stampa',
                body: [
                    'I file li teniamo **noi**: `ARCHIVIO_ESECUTIVI` dice dove, e quella cartella va nel backup — DecoNetwork li cancella dopo due settimane e non si riscaricano.',
                    '`NAS_ESECUTIVI` è il vecchio NAS di stampa. Resta per gli impianti che ce l’hanno già; se `ARCHIVIO_ESECUTIVI` è scritta, vince quella.',
                    'Le `FILE_ESECUTIVI_*` dicono come si chiamano le cartelle dei processi e ogni quanto gira lo scarico.',
                    '`ARCHIVIO_POTATURA_GIORNI` è **l’unica impostazione che cancella dei file**, e non ha rimedio. Vuota o zero: non si cancella mai, ed è così di predefinito.',
                    'Per vedere l’archivio c’è la schermata «Archivio esecutivi».',
                ],
            },
        ],
    },
    {
        k: 'ufficio-guasti',
        station: 'ufficio',
        title: 'Quando qualcosa non va',
        summary: 'Le quattro risposte del sistema, e cosa fare per ciascuna.',
        roles: ['amministratore'],
        sections: [
            {
                title: 'Il sistema risponde in quattro modi',
                body: [
                    'Sono distinti apposta: dal modo si capisce **di chi è il problema**.',
                    '**Rifiuto con un numero di regola** — il sistema ha capito e dice di no. Non è un guasto: la regola sta facendo il suo lavoro. La pagina «Quando il sistema dice di no» le elenca.',
                    '**Campi sbagliati** — qualcosa di scritto non va, e il messaggio dice quale campo.',
                    '**Riprova fra poco** — un sistema esterno non risponde (il catalogo, il magazzino). Aspetta e rifai il gesto: non c’è niente da sistemare qui.',
                    '**Guasto, con un codice corto** — è un problema nostro. Il codice è l’unica cosa che serve: annotalo e chiamaci.',
                ],
            },
            {
                title: 'Cosa raccontare quando chiami',
                body: [
                    'Il codice corto del guasto, se c’è.',
                    'Il numero della regola, se era un rifiuto.',
                    'Cosa stavi facendo, in una frase.',
                    'Non serve altro: con quel codice si ritrova tutto.',
                ],
            },
            {
                title: 'Cosa si perde e cosa no',
                body: [
                    'Il banco scrive su database: un riavvio non perde le cassette in corso.',
                    'Se `DATABASE_URL` è vuota, invece, tutto è in memoria e un riavvio azzera. È il modo delle dimostrazioni.',
                    'Le foto scattate al banco non si ricostruiscono: stanno in una cartella loro, e quella cartella va nel backup.',
                ],
            },
        ],
    },
];
//# sourceMappingURL=office.js.map